import { getApiClientVigo } from "./ApiClientVigo.js";
import { useRechnerStore } from '@/stores/rechner.js';
import { useMainStore } from '@/stores/useMainStore.js';
import { useAntragStore } from '@/stores/antrag.js';
import { DatumISO } from '@/helpers/hilfsfunktionen.js';
import moment from 'moment';

/**
 * Hilfsfunktion: Ermittelt ältestes Geburtsdatum aus items
 * @param {Array} items - Array von Personen mit geburtstag-Feld (Format: JJJJ-MM-TT)
 * @returns {string} - Ältestes Datum im Format JJJJ-MM-TT
 */
function getAeltestesGeburtsdatum(items) {
    let oldest = null;
    let oldestDateString = '';
    
    items.forEach(item => {
        if (item.geburtstag) {
            // geburtstag ist bereits im Format JJJJ-MM-TT
            const currentDate = new Date(item.geburtstag);
            if (!oldest || currentDate < oldest) {
                oldest = currentDate;
                oldestDateString = item.geburtstag;
            }
        }
    });
    
    return oldestDateString;
}

/**
 * Hilfsfunktion: Sammelt Tarife für eine Person (inkl. ermittelter Beitrag pro Tarif)
 * @param {Object} item - Person mit tarif1-8 und tarif1beitrag-tarif8beitrag Feldern
 * @param {string} variante - "V" oder "A"
 * @returns {Array} - Array von Tarif-Objekten
 */
function getTarifeForPerson(item, variante) {
    const tarife = [];
    if (item.tarif1 === 1) tarife.push({ tarifbezeichnung: "ZVS", Tarifvariante: variante, Beitrag: item.tarif1beitrag ?? 0 });
    if (item.tarif2 === 1) tarife.push({ tarifbezeichnung: "ZVP", Tarifvariante: variante, Beitrag: item.tarif2beitrag ?? 0 });
    if (item.tarif3 === 1) tarife.push({ tarifbezeichnung: "ZX2", Tarifvariante: variante, Beitrag: item.tarif3beitrag ?? 0 });
    if (item.tarif4 === 1) tarife.push({ tarifbezeichnung: "S1", Tarifvariante: variante, Beitrag: item.tarif4beitrag ?? 0 });
    if (item.tarif5 === 1) tarife.push({ tarifbezeichnung: "S2", Tarifvariante: variante, Beitrag: item.tarif5beitrag ?? 0 });
    if (item.tarif6 === 1) tarife.push({ tarifbezeichnung: "AR20", Tarifvariante: variante, Beitrag: item.tarif6beitrag ?? 0 });
    if (item.tarif7 === 1) tarife.push({ tarifbezeichnung: "ARNS", Tarifvariante: variante, Beitrag: item.tarif7beitrag ?? 0 });
    if (item.tarif8 === 1) tarife.push({ tarifbezeichnung: "ARNF", Tarifvariante: variante, Beitrag: item.tarif8beitrag ?? 0 });
    return tarife;
}

/**
 * Legt einen neuen Vertrag an
 * @param {string} antragId - Die AntragId (GUID)
 * @returns {Promise<{success: boolean, vertragsnummer?: number, secret?: string, error?: string}>}
 */
export async function createVertrag(antragId) {
    try {
        const client = getApiClientVigo();
        const rechnerStore = useRechnerStore();
        const mainStore = useMainStore();
        const antragStore = useAntragStore();
        const endpoint = '/v2/Tarifrechner/vertrag';
        
        // Mehrfachabsendung verhindern
        if (rechnerStore.antragAbgeschickt === true) {
            console.log('[VertragService] Antrag bereits abgeschickt, Abbruch');
            return {
                success: false,
                error: 'Antrag wurde bereits abgeschickt'
            };
        }
        
        // statusVerwendung hochzählen vor API-Call
        antragStore.statusVerwendung++;
        console.log('[VertragService] statusVerwendung erhöht auf:', antragStore.statusVerwendung);
        
        // Vertriebsweg berechnen
        let vertriebsweg = "DAVG";
        if (mainStore.kanal === "vigoselect") {
            if (rechnerStore.aok > 0) {
                vertriebsweg = "AOK";
            } else {
                vertriebsweg = "EndkundeAOK";
            }
        }
        
        // KennungTarif berechnen
        let kennungTarif = "Kombiabschluss";
        if (mainStore.rechner === "kombi" || mainStore.rechner === "kombistat") {
            kennungTarif = "Kombiabschluss";
        } else if (mainStore.rechner === "arn") {
            // Fallunterscheidung: ARNS (tarif7) oder ARNF (tarif8)
            if (rechnerStore.hatARNF()) {
                kennungTarif = "ARNF";
            } else if (rechnerStore.hatARNS()) {
                kennungTarif = "ARNS";
            } else {
                kennungTarif = "ARN"; // Fallback
            }
        } else if (mainStore.rechner === "ar20") {
            kennungTarif = "AR20";
        }
        
        // Tarifvariante
        // ARN (ARNS/ARNF): Immer Variante "V", unabhängig von Kanal und Vertriebsweg
        let variante = mainStore.kanal === "meinevigo" ? "V" : "A";
        if (mainStore.rechner === "arn") {
            variante = "V";
        }
        
        console.log('[VertragService] Vertriebsweg:', vertriebsweg);
        console.log('[VertragService] KennungTarif:', kennungTarif);
        console.log('[VertragService] Tarifvariante:', variante);
        
        // Versicherungsbeginn konvertieren
        const versicherungsbeginn = DatumISO(rechnerStore.versicherungsbeginn);
        
        // Ältestes Geburtsdatum ermitteln:
        // Bei ARNF: explizit eingegebenes Datum aus store verwenden (älteste Familienperson, nicht zwingend in items)
        // Fallback: ältestes Datum aus den items berechnen (ARNS, AR20 etc.)
        const geburtsdatumAeltestePerson = rechnerStore.geburtsdatumAeltestePerson
            ? DatumISO(rechnerStore.geburtsdatumAeltestePerson)
            : getAeltestesGeburtsdatum(rechnerStore.items);
        
        console.log('[VertragService] Versicherungsbeginn:', versicherungsbeginn);
        console.log('[VertragService] GeburtsdatumAeltestePerson:', geburtsdatumAeltestePerson);
        
        const vnItem = rechnerStore.items.find(i => i.id === rechnerStore.vndaten.id);
        // Versicherungsnehmer-Block
        const versicherungsnehmer = {
            Uuid: "",
            Anrede: rechnerStore.vndaten.anrede,
            Titel: rechnerStore.vndaten.titel || "",
            Vorname: rechnerStore.vndaten.vorname,
            Nachname: rechnerStore.vndaten.nachname,
            Strasse: rechnerStore.vndaten.strasse,
            Hausnummer: rechnerStore.vndaten.hausnummer,
            Postleitzahl: rechnerStore.vndaten.plz,
            Ort: rechnerStore.vndaten.ort,
            Land: rechnerStore.vndaten.land || "Deutschland",
            Staatsangehoerigkeit: (rechnerStore.vndaten.staatsangehoerigkeit || "").trim(),
            Telefonnummer: rechnerStore.vndaten.telefon,
            EMail: rechnerStore.vndaten.email,
            Krankenkasse: rechnerStore.vndaten.krankenkassenName || "",
            Aokversichertennummer: rechnerStore.vndaten.aoknummer || "",
            Einwilligungsozialdaten: rechnerStore.eweSozialdaten || false,
            Einwilligungkontaktzwecke: rechnerStore.eweKontakt || false,
            Geburtsdatum: rechnerStore.vndaten.geburtstag,
            Tarife: vnItem ? getTarifeForPerson(vnItem, variante) : []
        };
        
        // Vps-Array erstellen (nur Personen, die NICHT der VN sind)
        const vps = [];
        for (const item of rechnerStore.items) {
            if (item.id !== rechnerStore.vndaten.id) {
                const vpObject = {
                    Uuid: "",
                    Anrede: item.anrede,
                    Titel: item.titel || "",
                    Vorname: item.vorname,
                    Nachname: item.nachname,
                    Krankenkasse: item.krankenkassenName || "",
                    AokVersichertennummer: item.aoknummer || "",
                    Geburtsdatum: item.geburtstag,
                    Tarife: getTarifeForPerson(item, variante)
                };
                
                vps.push(vpObject);
            }
        }
        

        
        // Bankverbindung-Block
        const bankverbindung = {
            Anrede: rechnerStore.bankdaten.anrede,
            Titel: rechnerStore.bankdaten.titel || "",
            Vorname: rechnerStore.bankdaten.vorname,
            Nachname: rechnerStore.bankdaten.nachname,
            Strasse: rechnerStore.bankdaten.strasse,
            Hausnummer: rechnerStore.bankdaten.hausnummer,
            Postleitzahl: rechnerStore.bankdaten.plz,
            Ort: rechnerStore.bankdaten.ort,
            Land: rechnerStore.bankdaten.land || "Deutschland",
            Iban: rechnerStore.bankdaten.iban,
            Kreditinstitut: rechnerStore.ktobank || "",
            Kontoinhaberabweichend: rechnerStore.bankabweichend === 'ungleich'
        };
        
        // Request Body zusammenstellen
        const requestBody = {
            AntragId: antragId,
            Kanal: mainStore.kanal,
            Vertriebsweg: vertriebsweg,
            KennungTarif: kennungTarif,
            VermittlerNrMakler: "",
            VermittlerNrVertriebspartner: "",
            VermittlerUrl: "0",
            Aokmitarbeiter: rechnerStore.aok > 0,
            Rdnummer: parseInt(rechnerStore.aok) || 0,
            GsNummer: 0,
            Versicherungsbeginn: versicherungsbeginn,
            Versicherungsnehmeristvp: rechnerStore.VNIstVP(),
            Versicherungsnehmer: versicherungsnehmer,
            Vps: vps,
            Bankverbindung: bankverbindung
        };
        
        // GeburtsdatumAeltestePerson nur hinzufügen, wenn vorhanden
        if (geburtsdatumAeltestePerson) {
            requestBody.GeburtsdatumAeltestePerson = geburtsdatumAeltestePerson;
        }
        
        // Beratungsdokumentation nur bei AOK
        if (rechnerStore.aok > 0) {
            const beratungsdokumentation = {
                BeratungVomISO: moment().format('YYYYMMDD'),
                Beratungsart: rechnerStore.beratungsart == 1 ? "Telefon" : "Persönlich",
                Beratungsgrund: true,
                Empfehlung: rechnerStore.kundenentscheid === 0,
                AbweichendeEntscheidung: rechnerStore.kundenentscheid === 1,
                Anmerkungen: rechnerStore.kundenentscheid === 1 ? rechnerStore.abweichendText : "",
                Vollstaendig: rechnerStore.vollstaendig,
                EinwilligungWillenserklaerung: rechnerStore.eweWillenserklaerung || false,
                EinwilligungDatennutzung: rechnerStore.eweKontakt || false,
                RDNummer: String(rechnerStore.aok),
                RDBezeichnung: "",
                GSNummer: ""
            };
            requestBody.Beratungsdokumentation = beratungsdokumentation;
            console.log('[VertragService] Beratungsdokumentation hinzugefügt (AOK-Antrag)');
        }
        
        // Debug: Aufruf-URL und Body
        console.log(`[VertragService] POST ${endpoint}`);
        console.log('[VertragService] Request Body:', JSON.stringify(requestBody, null, 2));
        
        const response = await client.post(endpoint, requestBody, {
            validateStatus: function (status) {
                console.log(`[VertragService] Response Status: ${status}`);
                return status < 500;
            }
        });
        
        // Debug: Response JSON
        console.log('[VertragService] Response Data:', JSON.stringify(response.data, null, 2));
        
        // Erfolg prüfen: status === 200 && ok === true && returncode === 0
        if (response.status === 200 && response.data?.ok === true && response.data?.returncode === 0) {
            // Prüfe ob vertragsnummer und secret/secretstring in data oder data.data liegen
            const responseData = response.data.data || response.data;
            const vertragsnummer = responseData.vertragsnummer;
            const secret = responseData.secret || responseData.secretstring;
            
            if (vertragsnummer && secret) {
                console.log('[VertragService] Vertrag erfolgreich angelegt');
                console.log('[VertragService] Vertragsnummer:', vertragsnummer);
                console.log('[VertragService] Secret:', secret);
                
                return {
                    success: true,
                    vertragsnummer: vertragsnummer,
                    secret: secret
                };
            } else {
                console.error('[VertragService] Vertragsnummer oder Secret fehlt in Response');
                return {
                    success: false,
                    error: 'Vertragsdaten unvollständig in API-Response'
                };
            }
        } else {
            // API-Fehler mit detaillierter Meldung
            let errorMsg = response.data?.nachricht || 'Fehler beim Anlegen des Vertrags';
            if (response.data?.returncode) {
                errorMsg = `${errorMsg} (Code: ${response.data.returncode})`;
            }
            console.error('[VertragService] API Error:', errorMsg, 'ReturnCode:', response.data?.returncode);
            return {
                success: false,
                error: errorMsg
            };
        }
    }
    catch (error) {
        console.error('[VertragService] Exception beim Anlegen des Vertrags:', error.message);
        return {
            success: false,
            error: error.message || 'Netzwerkfehler beim Anlegen des Vertrags'
        };
    }
}

/**
 * Lädt den Versicherungsschein als PDF herunter
 * @param {number} vertragsnummer - Die Vertragsnummer
 * @param {string} secret - Das Secret (GUID mit Bindestrichen)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function downloadVersicherungsschein(vertragsnummer, secret) {
    try {
        const mainStore = useMainStore();
        
        // Kurze Pause vor dem Aufruf, um dem Backend Zeit zur Verarbeitung zu geben
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        const endpoint = '/v2/Tarifrechner/erzeugepolice';
        
        // API gibt direkt PDF zurück (inline im Browser)
        const baseURL = mainStore.apiVigotr || '';
        const apiKey = mainStore.apiKey || '';
        const normalizedBase = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        
        const params = new URLSearchParams({
            secret: secret,
            vertragId: vertragsnummer
        });
        
        const fullUrl = `${normalizedBase}${endpoint}?${params}`;
        
        
        
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'ApiKey': apiKey,
                'Accept': 'application/pdf'
            }
        });
        
        
        
        if (!response.ok) {
            console.error('[VertragService] Versicherungsschein-Download fehlgeschlagen, Status:', response.status);
            
            // Versuche JSON-Fehlerantwort zu lesen (für Fehlerfälle)
            let errorMsg = 'Versicherungsschein konnte nicht heruntergeladen werden';
            try {
                const errorData = await response.json();
                console.log('[VertragService] Error Response Data:', JSON.stringify(errorData, null, 2));
                if (errorData?.nachricht) {
                    errorMsg = `${errorMsg}: ${errorData.nachricht}`;
                }
                if (errorData?.returncode) {
                    errorMsg = `${errorMsg} (Code: ${errorData.returncode})`;
                }
            } catch (e) {
                // Falls keine JSON-Response, bleibt die generische Meldung
                console.log('[VertragService] Keine JSON-Fehlerantwort verfügbar');
            }
            
            return {
                success: false,
                error: errorMsg
            };
        }
        
        // Erfolgreicher Download - Response ist direkt das PDF als Blob
        const blob = await response.blob();
        
        
        // PDF-Download auslösen
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Versicherungsschein_${vertragsnummer}.pdf`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        
        
        return {
            success: true
        };
    }
    catch (error) {
        console.error('[VertragService] Exception beim Download:', error.message);
        return {
            success: false,
            error: error.message || 'Netzwerkfehler beim Herunterladen des Versicherungsscheins'
        };
    }
}
